/**
 * 音频播放Store (Pinia)
 * 音频状态管理功能
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

export const useAudioStore = defineStore('audio', () => {
  // ============= 状态定义 =============
  const currMp3Url = ref('');
  const currentSubtitleUrl = ref<string | undefined>(undefined); // ✅ 字幕 URL
  const currentSubtitleAssetId = ref<string | undefined>(undefined); // ✅ 字幕 Asset ID
  const currentAudioInfo = ref<any>(null);
  const currentAssetId = ref<string | undefined>(undefined); // 当前音频的 Asset ID
  const isPlaying = ref(false);
  const volume = ref(1.0);
  const currentTime = ref(0);
  const duration = ref(0);
  const playbackRate = ref(1.0);

  // 页面播放器引用（用于统一控制）
  const pageAudioPlayerRef = ref<HTMLAudioElement | null>(null);

  // ============= 计算属性 =============
  const currentAudioUrl = computed(() => currMp3Url.value);
  
  const hasAudio = computed(() => !!currMp3Url.value);
  
  const progress = computed(() => {
    if (duration.value === 0) return 0;
    return (currentTime.value / duration.value) * 100;
  });

  // ============= Actions =============
  
  /**
   * 设置当前音频
   */
  const setCurrentAudio = (audioData: { url?: string; subtitleUrl?: string; subtitleAssetId?: string; info?: any; assetId?: string }) => {
    if (audioData.url) {
      currMp3Url.value = audioData.url;
    }

    // 只在参数明确传递时才更新，否则保持原值
    if ('subtitleUrl' in audioData) {
      currentSubtitleUrl.value = audioData.subtitleUrl;
    }

    if ('subtitleAssetId' in audioData) {
      currentSubtitleAssetId.value = audioData.subtitleAssetId;
    }

    if (audioData.info) {
      currentAudioInfo.value = audioData.info;
    }

    if ('assetId' in audioData) {
      currentAssetId.value = audioData.assetId;
    }
  };
  
  /**
   * 播放音频
   */
  const play = () => {
    isPlaying.value = true;
  };
  
  /**
   * 暂停音频
   */
  const pause = () => {
    isPlaying.value = false;
  };
  
  /**
   * 停止音频
   */
  const stop = () => {
    isPlaying.value = false;
    currentTime.value = 0;
  };
  
  /**
   * 设置音量
   */
  const setVolume = (newVolume: number) => {
    volume.value = Math.max(0, Math.min(1, newVolume));
  };
  
  /**
   * 设置播放进度
   */
  const setCurrentTime = (time: number) => {
    currentTime.value = Math.max(0, Math.min(duration.value, time));
  };
  
  /**
   * 设置音频时长
   */
  const setDuration = (newDuration: number) => {
    duration.value = newDuration;
  };
  
  /**
   * 设置播放速率
   */
  const setPlaybackRate = (rate: number) => {
    playbackRate.value = Math.max(0.25, Math.min(4, rate));
  };
  
  /**
   * 清除当前音频
   */
  const clearAudio = () => {
    currMp3Url.value = '';
    currentSubtitleUrl.value = undefined;
    currentAudioInfo.value = null;
    isPlaying.value = false;
    currentTime.value = 0;
    duration.value = 0;
  };

  /**
   * 注册页面播放器引用
   * 用于让 useAudioPlayer 使用页面上的播放器元素
   */
  const registerPageAudioPlayer = (audioElement: HTMLAudioElement | null) => {
    pageAudioPlayerRef.value = audioElement;
  };

  /**
   * 获取页面播放器引用
   */
  const getPageAudioPlayer = () => {
    return pageAudioPlayerRef.value;
  };

  return {
    // 状态
    currMp3Url,
    currentSubtitleUrl,
    currentSubtitleAssetId,
    currentAudioInfo,
    currentAssetId,
    isPlaying,
    volume,
    currentTime,
    duration,
    playbackRate,

    // 计算属性
    currentAudioUrl,
    hasAudio,
    progress,

    // 方法
    setCurrentAudio,
    play,
    pause,
    stop,
    setVolume,
    setCurrentTime,
    setDuration,
    setPlaybackRate,
    clearAudio,
    registerPageAudioPlayer,
    getPageAudioPlayer
  };
});


